import { addUrlApi } from './utils'

/*
 * Google sign-in gate shown in front of the Odoo login form.
 *
 * IMPORTANT: this is a client-side gate, not an authentication factor. The ID
 * token Google returns is decoded here, never verified, and the app has no
 * backend of its own: Odoo's XML-RPC endpoint keeps accepting a username and
 * password from anything that can reach it, with no knowledge of this step. The
 * gate exists to keep technicians on their @zabatt.com account in the app, and
 * a determined user can skip it.
 *
 * Turning it into a real factor means verifying the ID token server side --
 * signature against Google's JWKS, `aud`, `iss`, `exp`, `email_verified` -- in
 * an Odoo method, and refusing to hand out the tech token when it fails. See
 * the "Google sign-in gate" section of README.md.
 */

export const GOOGLE_CLIENT_ID = ('' + (process.env.REACT_APP_GOOGLE_CLIENT_ID || '')).trim()

// Company policy rather than a per-environment setting, so it is not in .env.
export const ALLOWED_EMAIL_DOMAIN = 'zabatt.com'

// The gate is opt-in: only REACT_APP_REQUIRE_GOOGLE_SIGNIN=true (or 1/yes/on)
// turns it on, anything else -- the variable being absent included -- shows the
// Odoo login form straight away. Off by default because every origin the app is
// served from has to be listed in the OAuth client's *Authorized JavaScript
// origins*, and one that is not answers every sign-in with
// `Error 400: origin_mismatch`; a tunnel or a new host would otherwise lock the
// login screen until someone edits the Google Cloud console. Defaulting to off
// costs no security: the gate is a client-side check, see the note above.
export const GOOGLE_SIGNIN_REQUIRED = ['true', '1', 'yes', 'on'].includes(
    ('' + (process.env.REACT_APP_REQUIRE_GOOGLE_SIGNIN || '')).trim().toLowerCase()
)

const GSI_CLIENT_URL = 'https://accounts.google.com/gsi/client'

export type GoogleIdentity = {
    email: string,
    name: string,
    picture: string,
    hd: string,
    emailVerified: boolean,
}

let loadPromise: Promise<any> | null = null

// Resolves with the `google` global once accounts.google.com/gsi/client is
// loaded. The promise is cached so a remount does not inject a second script.
export function loadGoogleIdentityServices(): Promise<any> {
    if (loadPromise)
        return loadPromise
    loadPromise = new Promise((resolve, reject) => {
        const google = (window as any).google;
        if (google && google.accounts && google.accounts.id) {
            resolve(google);
            return;
        }
        // addUrlApi returns false when the tag is already there; with the cached
        // promise above that only happens if something else injected it.
        const script = addUrlApi(GSI_CLIENT_URL)
        if (!script) {
            reject(new Error('Google Identity Services script is already loading'));
            return;
        }
        script.onload = () => resolve((window as any).google)
        script.onerror = () => {
            loadPromise = null;
            reject(new Error('Could not load Google Identity Services'));
        }
    })
    return loadPromise
}

// Drops the remembered account so the chooser is shown again on the next click.
export function forgetGoogleAccount(): void {
    const google = (window as any).google;
    if (google && google.accounts && google.accounts.id)
        google.accounts.id.disableAutoSelect();
}

function base64UrlDecode(value: string): string {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
    const bytes = atob(base64 + '==='.slice((base64.length + 3) % 4))
    // atob yields one character per byte; re-read them as UTF-8 so that
    // accented names and non-ASCII domains survive.
    return decodeURIComponent(bytes.split('').map(
        char => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2)
    ).join(''))
}

// Reads the claims out of the ID token. Decoding only -- see the note above.
export function identityFromCredential(credential: string): GoogleIdentity {
    const parts = ('' + credential).split('.')
    if (parts.length !== 3)
        throw new Error('Malformed Google credential')
    const claims = JSON.parse(base64UrlDecode(parts[1]))
    return {
        email: '' + (claims.email || ''),
        name: '' + (claims.name || ''),
        picture: '' + (claims.picture || ''),
        hd: '' + (claims.hd || ''),
        emailVerified: claims.email_verified === true || claims.email_verified === 'true',
    }
}

export function isAllowedDomain(identity: GoogleIdentity): boolean {
    if (!identity.emailVerified)
        return false
    const parts = identity.email.toLowerCase().split('@')
    return parts.length === 2 && parts[0].length > 0 && parts[1] === ALLOWED_EMAIL_DOMAIN
}
